import React from "react";
import { Tag, Tooltip, Typography } from "antd";
import AttachFileIcon from "@material-ui/icons/AttachFile";

const { Text } = Typography;

export const AVATAR_COLORS = [
  "#2f7bf5",
  "#7c4dff",
  "#0ea5a4",
  "#f59e0b",
  "#e0568a",
  "#16a34a",
  "#f2683c",
  "#4b6bfb",
];

export function avatarColor(seed) {
  let s = `${seed || ""}`;
  let sum = 0;
  for (let i = 0; i < s.length; i++) {
    sum += s.charCodeAt(i);
  }
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

export function initials(name) {
  let parts = `${name || ""}`.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function Muted({ children, ...rest }) {
  return (
    <div className="cert-muted" {...rest}>
      {children}
    </div>
  );
}

export function Applicant({ el }) {
  return (
    <div className="cert-applicant">
      <div
        className="cert-avatar"
        style={{ background: avatarColor(el.name || el.email) }}
      >
        {initials(el.name)}
      </div>
      <div>
        <div className="cert-name">{el.name}</div>
        <div className="cert-muted">
          {el.gender || "—"} &middot; Roll {el.roll_no || "—"}
        </div>
      </div>
    </div>
  );
}

export function ApprovalTag({ status, on, by }) {
  if (status == 1) {
    return (
      <div>
        <Tag color="green" style={{ marginInlineEnd: 0 }}>
          Approved
        </Tag>
        {on && <div className="cert-muted">{on}</div>}
        {by && <div className="cert-muted">by {by}</div>}
      </div>
    );
  }
  if (status == 2) {
    return (
      <Tag color="red" style={{ marginInlineEnd: 0 }}>
        Rejected
      </Tag>
    );
  }
  return (
    <Tag color="gold" style={{ marginInlineEnd: 0 }}>
      Pending
    </Tag>
  );
}

export function RequestStatusTag({ el }) {
  if (el.status == 1) {
    return (
      <div>
        <Tag color="green" style={{ marginInlineEnd: 0 }}>
          Generated
        </Tag>
        {el.generated_on && <div className="cert-muted">{el.generated_on}</div>}
      </div>
    );
  }
  if (el.status == 0) {
    return (
      <Tag color="gold" style={{ marginInlineEnd: 0 }}>
        Pending
      </Tag>
    );
  }
  return (
    <div>
      <Tag color="red" style={{ marginInlineEnd: 0 }}>
        Cancelled
      </Tag>
      {el.cancel_reason && (
        <Tooltip title={el.cancel_reason}>
          <div className="cert-muted cert-clamp">{el.cancel_reason}</div>
        </Tooltip>
      )}
    </div>
  );
}

export function PaymentCell({ el }) {
  if (el.payment_status == 1) {
    return (
      <Tooltip title="Open payment receipt">
        <a
          target="_blank"
          href={`/certificate/receipt-print?certificate_request_id=${el.id}`}
        >
          <Tag color="#16a34a" style={{ marginInlineEnd: 0 }}>
            Rs. {el.amount_paid}
          </Tag>
          <div className="cert-muted">{el.amount_paid_on}</div>
        </a>
      </Tooltip>
    );
  }
  return (
    <Tag color="red" style={{ marginInlineEnd: 0 }}>
      Not Paid
    </Tag>
  );
}

export function DocumentsCell({ el }) {
  let marksheet =
    Array.isArray(el.marksheet_file_url) && el.marksheet_file_url.length > 0
      ? el.marksheet_file_url[0]
      : "";
  let registration =
    Array.isArray(el.registration_file_url) &&
    el.registration_file_url.length > 0
      ? el.registration_file_url[0]
      : "";

  if (!marksheet && !registration) {
    return <Text type="secondary">—</Text>;
  }

  return (
    <div>
      {marksheet && (
        <div>
          <a className="cert-doc-link" target="_blank" href={marksheet}>
            <AttachFileIcon style={{ fontSize: 14 }} /> Marksheet
          </a>
        </div>
      )}
      {registration && (
        <div style={{ marginTop: marksheet ? 4 : 0 }}>
          <a className="cert-doc-link" target="_blank" href={registration}>
            <AttachFileIcon style={{ fontSize: 14 }} /> Registration
          </a>
        </div>
      )}
    </div>
  );
}

export function AddressCell({ address }) {
  if (!address) {
    return <Text type="secondary">—</Text>;
  }
  return (
    <Tooltip title={address}>
      <div className="cert-muted cert-clamp">{address}</div>
    </Tooltip>
  );
}

export function statusText(el) {
  return el.status == 0
    ? "Pending"
    : el.status == 1
    ? "Generated"
    : `Cancelled, ${el.cancel_reason || ""}`;
}

export function approvalText(status, on) {
  return status == 0
    ? "Pending"
    : status == 1
    ? `Approved on ${on || ""}`
    : "Rejected";
}

export function paymentText(el) {
  return el.payment_status == 1
    ? `Rs. ${el.amount_paid} on ${el.amount_paid_on}`
    : "Not Paid";
}

export function downloadCsv(filename, headers, rows) {
  let escape = (v) => {
    let s = v == null ? "" : `${v}`;
    return `"${s.replace(/"/g, '""').replace(/\r?\n/g, " ")}"`;
  };

  let csv = [headers.map(escape).join(",")]
    .concat(rows.map((r) => r.map(escape).join(",")))
    .join("\n");

  let blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  let url = URL.createObjectURL(blob);
  let a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function matchesSearch(el, query, extraFields) {
  let q = `${query}`.trim().toLowerCase();
  if (q === "") {
    return true;
  }
  let fields = [
    el.name,
    el.email,
    el.phone,
    el.roll_no,
    el.type_name,
    el.course,
    el.father_name,
    el.mother_name,
    el.address,
    el.certificate_sl_no,
    el.now_at,
  ].concat(extraFields || []);

  return fields
    .filter((v) => v != null)
    .join(" ")
    .toLowerCase()
    .includes(q);
}
